import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";

// Maximum number of admins allowed in the system. This is a business rule to prevent too many users from having admin privileges, which could pose security risks.
const MAX_ADMINS = 3;

async function updateUserRole({ userId, newRole }) {
  try {
    // ── Before making someone an admin ───────────────────────────
    // Check how many admins currently exist.
    // We do this so we can show a clear message if the limit
    // has already been reached.
    if (newRole === "admin") {
      const { count, error: countError } = await supabase
        .from("users")
        .select("*", { count: "exact", head: true }) // Count admins only, no need to fetch their data
        .eq("role", "admin");

      // If something unexpected happens, show a simple message
      if (countError) throw new Error("Could not verify admin count.");

      // Stop if we've reached the maximum number of admins
      if (count >= MAX_ADMINS) {
        throw new Error(`Admin limit reached. Only ${MAX_ADMINS} admins are allowed.`);
      }
    }

    // ── Update the user's role ──────────────────────────────────
    // Change the selected user's role in the database.
    const { error } = await supabase.from("users").update({ role: newRole }).eq("id", userId);

    if (error) throw new Error(error?.message);
  } catch (error) {
    // Keep any existing error message so it can be shown to the user
    if (error instanceof Error) {
      throw error;
    }

    // Fallback message for unexpected errors
    throw new Error("Unable to update user role.");
  }
}

function useUpdateUserRole() {
  // We need this to refresh the users list after a role update so the UI shows the new role immediately without needing a manual refresh.
  const queryClient = useQueryClient();

  const {
    mutate: updateRole,
    isPending,
    error,
  } = useMutation({
    mutationFn: updateUserRole,

    onSuccess: () => {
      // The update was successful, refresh the users list so the table immediately shows the user's new role.
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });

  return {
    updateRole,
    isUpdatingRole: isPending,
    errorUpdatingRole: error?.message ?? null,
  };
}

export default useUpdateUserRole;
